<template>
    <div id="subjects" class="departments-page">
        <div class="departments-header">
            <a-input-search
                v-model="searchText"
                :placeholder="$t('message.search')"
                class="search-input departments-search"
                @search="onSearch"
            />
            <div class="departments-header__right">
                <SupsisButton small ghost @click="collapseAll">
                    <md-icon>unfold_less</md-icon>
                    {{ $t("message.collapseAll") }}
                </SupsisButton>
                <SupsisButton small ghost @click="expandAll">
                    <md-icon>unfold_more</md-icon>
                    {{ $t("message.expandAll") }}
                </SupsisButton>
                <SupsisButton
                    ghost
                    @click="$feature.beforeCreateDepartment(showModalWithoutEdit, paginationCursor.total)"
                >
                    <md-icon>add</md-icon>
                    {{ $t("message.createDepartment") }}
                </SupsisButton>
            </div>
        </div>

        <div class="department-groups">
            <div
                v-for="(usersInGroup, groupKey) in departmentGroups"
                :key="groupKey"
                class="department-card"
                :class="{ 'department-card--no-dept': groupKey === noDepartmentKey }"
            >
                <div
                    class="department-card__header"
                    :style="groupKey !== noDepartmentKey ? { borderLeftColor: getGroupColor(groupKey) } : {}"
                    @click="toggleGroup(groupKey)"
                >
                    <div class="department-card__title-row">
                        <md-icon class="department-card__toggle">{{
                            expandedGroups.has(groupKey) ? "expand_more" : "chevron_right"
                        }}</md-icon>
                        <span class="department-card__name">{{ getGroupDisplayName(groupKey) }}</span>
                        <span class="department-card__count">{{ usersInGroup.length }}</span>
                    </div>
                    <div class="department-card__actions" @click.stop>
                        <template v-if="groupKey !== noDepartmentKey && getSubjectByKey(groupKey)">
                            <a-button
                                v-if="checkPermission(PERMISSION_TYPES['topic.update'].tr.name)"
                                type="link"
                                size="small"
                                class="action-btn"
                                @click="edit(getSubjectByKey(groupKey)._id)"
                            >
                                <span class="material-icons">edit</span>
                            </a-button>
                            <a-button
                                v-if="checkPermission(PERMISSION_TYPES['topic.delete'].tr.name)"
                                type="link"
                                size="small"
                                class="action-btn action-btn--danger"
                                @click="remove(getSubjectByKey(groupKey)._id)"
                            >
                                <ActionDelete />
                            </a-button>
                        </template>
                    </div>
                </div>

                <div v-show="expandedGroups.has(groupKey)" class="department-card__body">
                    <div class="department-card__user-actions" v-if="canUpdateUser">
                        <SupsisButton
                            v-if="groupKey !== noDepartmentKey"
                            xsmall
                            ghost
                            @click="openAddUsersModal(groupKey)"
                        >
                            <md-icon>person_add</md-icon>
                            {{ $t("message.addUsersToDepartment") }}
                        </SupsisButton>
                        <SupsisButton
                            v-if="usersInGroup.length > 0 && !isAllSelectedInGroup(groupKey)"
                            xsmall
                            ghost
                            @click="selectAllInGroup(groupKey)"
                        >
                            <md-icon>select_all</md-icon>
                            {{ $t("message.selectAll") }}
                        </SupsisButton>
                        <SupsisButton
                            v-if="selectedUserIdsInGroup(groupKey).length > 0"
                            xsmall
                            ghost
                            @click="deselectAllInGroup(groupKey)"
                        >
                            <md-icon>deselect</md-icon>
                            {{ $t("message.deselectAll") }}
                        </SupsisButton>
                        <SupsisButton
                            v-if="groupKey !== noDepartmentKey && selectedUserIdsInGroup(groupKey).length > 0"
                            xsmall
                            ghost
                            danger
                            @click="confirmRemoveFromDepartment(groupKey)"
                        >
                            <md-icon>person_remove</md-icon>
                            {{ $t("message.removeFromDepartment") }} ({{ selectedUserIdsInGroup(groupKey).length }})
                        </SupsisButton>
                        <a-dropdown
                            v-if="groupKey !== noDepartmentKey && selectedUserIdsInGroup(groupKey).length > 0"
                            :trigger="['click']"
                        >
                            <SupsisButton xsmall ghost>
                                <md-icon>drive_file_move</md-icon>
                                {{ $t("message.moveToDepartment") }} ({{ selectedUserIdsInGroup(groupKey).length }})
                            </SupsisButton>
                            <a-menu slot="overlay" @click="({ key }) => moveUsersDirectly(groupKey, key)">
                                <a-menu-item key="__all__">
                                    {{ $t("message.moveToAllDepartments") }}
                                </a-menu-item>
                                <a-menu-divider />
                                <a-menu-item
                                    v-for="s in getOtherSubjects(groupKey)"
                                    :key="s._id"
                                >
                                    {{ getSubjectName(s) }}
                                </a-menu-item>
                            </a-menu>
                        </a-dropdown>
                        <a-dropdown
                            v-if="groupKey !== noDepartmentKey && selectedUserIdsInGroup(groupKey).length > 0"
                            :trigger="['click']"
                        >
                            <SupsisButton xsmall ghost>
                                <md-icon>content_copy</md-icon>
                                {{ $t("message.copyToDepartment") }} ({{ selectedUserIdsInGroup(groupKey).length }})
                            </SupsisButton>
                            <a-menu slot="overlay" @click="({ key }) => copyUsersToDepartment(groupKey, key)">
                                <a-menu-item key="__all__">
                                    {{ $t("message.copyToAllDepartments") }}
                                </a-menu-item>
                                <a-menu-divider />
                                <a-menu-item
                                    v-for="s in getOtherSubjects(groupKey)"
                                    :key="s._id"
                                >
                                    {{ getSubjectName(s) }}
                                </a-menu-item>
                            </a-menu>
                        </a-dropdown>
                        <!-- Tüm Departmanlar grubunda: başka departmana taşı -->
                        <a-dropdown
                            v-if="groupKey === noDepartmentKey && selectedUserIdsInGroup(groupKey).length > 0"
                            :trigger="['click']"
                        >
                            <SupsisButton xsmall ghost>
                                <md-icon>drive_file_move</md-icon>
                                {{ $t("message.moveToDepartment") }} ({{ selectedUserIdsInGroup(groupKey).length }})
                            </SupsisButton>
                            <a-menu slot="overlay" @click="({ key }) => assignUsersToDepartment(groupKey, key)">
                                <a-menu-item
                                    v-for="s in undeleted"
                                    :key="s._id"
                                >
                                    {{ getSubjectName(s) }}
                                </a-menu-item>
                            </a-menu>
                        </a-dropdown>
                    </div>

                    <div class="department-card__user-list">
                        <div
                            v-for="user in usersInGroup"
                            :key="user._id"
                            class="department-user-item"
                            :class="{ selected: isUserSelectedForAction(user._id) }"
                        >
                            <a-checkbox
                                v-if="canUpdateUser"
                                :checked="isUserSelectedForAction(user._id)"
                                @change="toggleUserSelection(user._id)"
                            />
                            <AgentAvatar :userID="user._id" :size="32" :isNamed="false" />
                            <span class="department-user-item__name">{{ user.name || user.email }}</span>
                            <div class="department-user-item__actions" v-if="canUpdateUser && groupKey !== noDepartmentKey && getSubjectByKey(groupKey)">
                                <a-dropdown :trigger="['click']">
                                    <a-button type="link" size="small">
                                        <md-icon>more_vert</md-icon>
                                    </a-button>
                                    <a-menu slot="overlay">
                                        <a-menu-item @click="removeUserFromDepartment(user, groupKey)">
                                            <md-icon>person_remove</md-icon>
                                            {{ $t("message.removeFromDepartment") }}
                                        </a-menu-item>
                                        <a-sub-menu :title="$t('message.moveToDepartment')">
                                            <a-menu-item
                                                key="move-all"
                                                @click="moveSingleUser(user, groupKey, '__all__')"
                                            >
                                                {{ $t("message.moveToAllDepartments") }}
                                            </a-menu-item>
                                            <a-menu-divider />
                                            <a-menu-item
                                                v-for="s in getOtherSubjects(groupKey)"
                                                :key="'move-' + s._id"
                                                @click="moveSingleUser(user, groupKey, s._id)"
                                            >
                                                {{ getSubjectName(s) }}
                                            </a-menu-item>
                                        </a-sub-menu>
                                        <a-sub-menu :title="$t('message.copyToDepartment')">
                                            <a-menu-item
                                                v-for="s in getOtherSubjects(groupKey)"
                                                :key="'copy-' + s._id"
                                                @click="copySingleUserToDepartment(user, s._id)"
                                            >
                                                {{ getSubjectName(s) }}
                                            </a-menu-item>
                                        </a-sub-menu>
                                    </a-menu>
                                </a-dropdown>
                            </div>
                            <div class="department-user-item__actions" v-if="canUpdateUser && groupKey === noDepartmentKey">
                                <a-dropdown :trigger="['click']">
                                    <a-button type="link" size="small">
                                        <md-icon>more_vert</md-icon>
                                    </a-button>
                                    <a-menu slot="overlay">
                                        <a-sub-menu :title="$t('message.moveToDepartment')">
                                            <a-menu-item
                                                v-for="s in undeleted"
                                                :key="'move-nd-' + s._id"
                                                @click="assignSingleUserToDepartment(user, s._id)"
                                            >
                                                {{ getSubjectName(s) }}
                                            </a-menu-item>
                                        </a-sub-menu>
                                    </a-menu>
                                </a-dropdown>
                            </div>
                        </div>
                        <div v-if="usersInGroup.length === 0" class="department-card__empty">
                            {{ $t("message.noUsersInDepartment") }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <a-modal
            v-if="isModalShow"
            :visible="isModalShow"
            :title="editMode ? $t('message.updateDepartmentForm') : $t('message.newDepartmentForm')"
            @cancel="hideModal"
            :footer="null"
            :width="600"
            wrapClassName="subject-form-modal"
        >
            <div id="modal-inner">
                <a-alert
                    v-if="$validator.errors.items.length && submitClicked"
                    :key="`alertContainer-${$i18n.locale}`"
                    :message="$t('message.error')"
                    class="mb-2"
                    showIcon
                    type="error"
                >
                    <div slot="description">
                        <div v-for="(err, idx) of $validator.errors.items" :key="idx">
                            <span>{{ err.msg }}</span>
                        </div>
                    </div>
                </a-alert>
                <b-form class="d-flex flex-column justify-content-center" @submit.prevent="duplicateControl">
                    <b-row>
                        <b-col md="12">
                            <b-row>
                                <b-col md="12">
                                    <MultiContent v-model="subjectInitialObject" ref="MultiContent" />
                                </b-col>
                                <b-col class="mt-3" md="12">
                                    <b-form-group :label="$t('report.status')">
                                        <b-form-radio-group v-model="subject.isActive" name="status" required>
                                            <b-form-radio :value="true">{{ $t("message.active") }}</b-form-radio>
                                            <b-form-radio :value="false">{{ $t("message.passive") }}</b-form-radio>
                                        </b-form-radio-group>
                                    </b-form-group>
                                </b-col>
                                <b-col md="12">
                                    <b-button
                                        :disabled="loading"
                                        type="submit"
                                        variant="primary"
                                        v-if="checkPermission(PERMISSION_TYPES['topic.update'].tr.name)"
                                    >
                                        <template v-if="!loading">
                                            <span v-if="!editMode">{{ $t("message.save") }}</span>
                                            <span v-else>{{ $t("message.update") }}</span>
                                        </template>
                                        <template v-else>
                                            <font-awesome-icon icon="spinner" spin />
                                        </template>
                                    </b-button>
                                    <b-button @click="hideModal">{{ $t("message.cancel") }}</b-button>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </b-form>
            </div>
        </a-modal>

        <a-modal
            :title="$t('message.addUsersToDepartment')"
            :visible="showAddUsersModal"
            :footer="null"
            @cancel="closeAddUsersModal"
            :width="400"
            wrapClassName="add-users-modal-wrap"
        >
            <div class="add-users-modal">
                <AgentPicker
                    ref="addUsersPicker"
                    v-model="addUsersSelected"
                    :omitItems="getUserIdsInCurrentGroup()"
                />
                <div class="add-users-modal__footer">
                    <SupsisButton ghost @click="closeAddUsersModal">{{ $t("message.cancel") }}</SupsisButton>
                    <SupsisButton primary :disabled="addUsersLoading" @click="confirmAddUsersToDepartment">
                        {{ $t("message.save") }}
                    </SupsisButton>
                </div>
            </div>
        </a-modal>

        <a-modal
            :title="$t('message.bulkUpdateProgress')"
            :visible="bulkProgress.visible"
            :footer="null"
            :closable="!bulkProgress.loading"
            :maskClosable="false"
        >
            <div class="progress-modal">
                <a-progress
                    :percent="bulkProgress.total ? Math.round((bulkProgress.current / bulkProgress.total) * 100) : 0"
                    status="active"
                />
                <p class="progress-modal__text">
                    {{ bulkProgress.current }} / {{ bulkProgress.total }} {{ $t("message.usersUpdated") }}
                </p>
            </div>
        </a-modal>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { log2 } from "../../../../../utils/log2";
import * as _ from "lodash";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import ActionEdit from "../../../../../components/svg/ActionEdit.vue";
import ActionDelete from "../../../../../components/svg/ActionDelete.vue";
import { toArray2, toObject2 } from "../../../../../utils/languageContent";
import MultiContent from "../../../../../components/MultiContent.vue";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";
import SupsisButton from "../../components/SupsisButton.vue";
import AgentAvatar from "../../../../../components/common/AgentAvatar.vue";
import AgentPicker from "../../../../../components/pickers/AgentPicker.vue";
import { SanitizeHtml } from "../../../../../utils/utils";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);
const log = log2("Subjects");
const subjectInitialObject = {
    _id: null,
    languageContent: {
        tr: { name: "", description: "" },
        en: { name: "", description: "" },
        ru: { name: "", description: "" },
        fr: { name: "", description: "" },
        es: { name: "", description: "" },
        ar: { name: "", description: "" },
        it: { name: "", description: "" },
    },
    isActive: true,
};

export default {
    name: "Subjects",
    components: {
        SupsisButton,
        MultiContent,
        ActionEdit,
        ActionDelete,
        AgentAvatar,
        AgentPicker,
    },
    data() {
        return {
            editID: null,
            isBusy: true,
            loading: false,
            editMode: false,
            subject: _.cloneDeep(subjectInitialObject),
            submitClicked: false,
            searchText: undefined,
            subjectTitle: "",
            isModalShow: false,
            queryAction: this.$route.query.action,
            paginationCursor: { total: 0, page: 1, perPage: 9999 },
            expandedGroups: new Set(),
            usersList: [],
            usersLoaded: false,
            selectedUserIds: [],
            showAddUsersModal: false,
            addUsersGroupKey: null,
            addUsersSelected: [],
            addUsersLoading: false,
            moveLoading: false,
            bulkProgress: { visible: false, current: 0, total: 0, loading: false },
        };
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        noDepartmentKey() {
            return this.$t("message.noDepartment");
        },
        canUpdateUser() {
            return this.checkPermission(PERMISSION_TYPES["user.update"].tr.name);
        },
        ...mapState({
            subjects: (state) => state.subject.subjects,
            languages: (state) => state.language.languages,
            site: (state) => state.site.site,
            isAdmin: (state) => state.auth.adminUser != null,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
            systemLanguages: (state) => state.setting.settings.systemLanguages,
        }),
        ...mapGetters({
            undeleted: "subject/getUndeleted",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        subjectInitialObject: {
            get() {
                if (!this.systemLanguages) return [];
                return [
                    {
                        type: "input",
                        field: "name",
                        label: this.$t("message.departmentTitle"),
                        placeholder: this.$t("message.departmentTitle"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.subject.languageContent, `[${lang}].name`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                    {
                        type: "rich",
                        field: "description",
                        label: this.$t("message.description"),
                        placeholder: this.$t("message.description"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.subject.languageContent, `[${lang}].description`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.subject.languageContent = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
        subjectInitialObjectTitle: {
            get() {
                return toArray2(this.subject, "languageContent", "name").languageContent;
            },
            set(value) {
                this.subject.languageContent = toObject2(value, "name");
            },
        },
        subjectInitialObjectContent: {
            get() {
                return toArray2(this.subject, "languageContent", "description").languageContent;
            },
            set(value) {
                this.subject.languageContent = toObject2(value, "description");
                this.addNewSubjectTitle();
            },
        },
        departmentGroups() {
            const groups = {};
            const noKey = this.noDepartmentKey;
            const filteredSubjects = this.searchText
                ? this.undeleted.filter((x) => {
                      const name = _.get(x, `languageContent.${this.$i18n.locale}.name`, "") || _.get(x, "languageContent.tr.name", "");
                      return String(name).toLowerCase().includes((this.searchText || "").toLowerCase());
                  })
                : this.undeleted;

            groups[noKey] = [];
            filteredSubjects.forEach((s) => {
                const name = this.getSubjectDisplayName(s);
                groups[name] = [];
            });

            this.usersList.forEach((user) => {
                const subs = user.subjects;
                const hasNoDept = !subs || subs.length === 0;
                if (hasNoDept) {
                    groups[noKey].push(user);
                } else {
                    subs.forEach((subjectId) => {
                        const subject = this.undeleted.find((x) => x._id === subjectId);
                        if (subject) {
                            const name = this.getSubjectDisplayName(subject);
                            if (groups[name]) groups[name].push(user);
                        }
                    });
                }
            });

            return groups;
        },
        allExpanded() {
            const keys = Object.keys(this.departmentGroups);
            return keys.length > 0 && keys.every((k) => this.expandedGroups.has(k));
        },
    },
    methods: {
        ...mapActions({
            getSubjects: "subject/getSubjects",
            getSubject: "subject/getSubject",
            createSubject: "subject/createSubject",
            updateSubject: "subject/updateSubject",
            deleteSubject: "subject/deleteSubject",
            getUsers: "user/getUsers",
            updateUser: "user/updateUser",
        }),
        getSubjectDisplayName(s) {
            return _.get(s, `languageContent.${this.$i18n.locale}.name`) || _.get(s, "languageContent.tr.name") || s._id;
        },
        getSubjectName(s) {
            return this.getSubjectDisplayName(s);
        },
        getGroupDisplayName(groupKey) {
            if (groupKey === this.noDepartmentKey) return this.$t("message.allDepartments");
            return groupKey;
        },
        getSubjectByKey(groupKey) {
            if (groupKey === this.noDepartmentKey) return null;
            return this.undeleted.find((s) => this.getSubjectDisplayName(s) === groupKey);
        },
        getGroupColor(groupKey) {
            let hash = 0;
            for (let i = 0; i < groupKey.length; i++) hash = groupKey.charCodeAt(i) + ((hash << 5) - hash);
            const hue = Math.abs(hash % 360);
            return `hsl(${hue}, 45%, 55%)`;
        },
        toggleGroup(key) {
            const next = new Set(this.expandedGroups);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            this.expandedGroups = next;
            this.$forceUpdate();
        },
        collapseAll() {
            this.expandedGroups = new Set();
            this.$forceUpdate();
        },
        expandAll() {
            this.expandedGroups = new Set(Object.keys(this.departmentGroups));
            this.$forceUpdate();
        },
        selectedUserIdsInGroup(groupKey) {
            const usersInGroup = this.departmentGroups[groupKey] || [];
            const ids = usersInGroup.map((u) => u._id);
            return this.selectedUserIds.filter((id) => ids.indexOf(id) !== -1);
        },
        isUserSelectedForAction(userId) {
            return this.selectedUserIds.indexOf(userId) !== -1;
        },
        toggleUserSelection(userId) {
            const idx = this.selectedUserIds.indexOf(userId);
            if (idx !== -1) this.selectedUserIds.splice(idx, 1);
            else this.selectedUserIds.push(userId);
            this.$forceUpdate();
        },
        selectUserForMove(userId) {
            if (this.selectedUserIds.indexOf(userId) === -1) this.selectedUserIds.push(userId);
        },
        isAllSelectedInGroup(groupKey) {
            var usersInGroup = this.departmentGroups[groupKey] || [];
            if (usersInGroup.length === 0) return false;
            var selected = this.selectedUserIdsInGroup(groupKey);
            return selected.length === usersInGroup.length;
        },
        selectAllInGroup(groupKey) {
            var usersInGroup = this.departmentGroups[groupKey] || [];
            var self = this;
            usersInGroup.forEach(function (u) {
                if (self.selectedUserIds.indexOf(u._id) === -1) self.selectedUserIds.push(u._id);
            });
            this.$forceUpdate();
        },
        deselectAllInGroup(groupKey) {
            var usersInGroup = this.departmentGroups[groupKey] || [];
            var ids = usersInGroup.map(function (u) { return u._id; });
            this.selectedUserIds = this.selectedUserIds.filter(function (id) { return ids.indexOf(id) === -1; });
            this.$forceUpdate();
        },
        getUserIdsInCurrentGroup() {
            if (!this.addUsersGroupKey) return [];
            const users = this.departmentGroups[this.addUsersGroupKey];
            return (users || []).map((u) => u._id);
        },
        openAddUsersModal(groupKey) {
            this.addUsersGroupKey = groupKey;
            this.addUsersSelected = [];
            this.showAddUsersModal = true;
        },
        closeAddUsersModal() {
            this.showAddUsersModal = false;
            this.addUsersGroupKey = null;
            this.addUsersSelected = [];
        },
        confirmAddUsersToDepartment() {
            const sub = this.getSubjectByKey(this.addUsersGroupKey);
            if (!sub || !this.canUpdateUser) return this.closeAddUsersModal();
            const userIds = (this.addUsersSelected || []).map((x) => (x && x.value) ? x.value : x).filter(Boolean);
            if (userIds.length === 0) {
                this.$message.warning(this.$t("message.selectAtLeastOneUser"));
                return;
            }
            this.addUsersLoading = true;
            const targetId = sub._id;
            const updateFns = userIds.map((uid) => () => {
                const user = this.usersList.find((u) => u._id === uid);
                if (!user) return Promise.resolve();
                const current = user.subjects || [];
                const next = current.indexOf(targetId) !== -1 ? current : current.concat([targetId]);
                return this.updateUser({ user: Object.assign({}, user, { subjects: next }), siteId: this.site._id });
            });
            this.closeAddUsersModal();
            this.runBulkUpdates(updateFns, () => {
                this.$toasted.show(this.$t("message.departmentUsersUpdated"), { duration: 3000, position: "bottom-center" });
                this.loadUsers();
                this.addUsersLoading = false;
            });
        },
        removeUserFromDepartment(user, groupKey) {
            const sub = this.getSubjectByKey(groupKey);
            if (!sub || !this.canUpdateUser) return;
            this.$dialog
                .confirm(this.$t("message.removeUserFromDepartmentConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    const next = (user.subjects || []).filter((id) => id !== sub._id);
                    return this.updateUser({ user: Object.assign({}, user, { subjects: next }), siteId: this.site._id });
                })
                .then(() => {
                    this.$toasted.show(this.$t("message.departmentUsersUpdated"), { duration: 3000, position: "bottom-center" });
                    this.loadUsers();
                })
                .catch((err) => {
                    if (err && err !== "cancel") {
                        log.error(err);
                        this.$toasted.error(this.$t("message.error"), { duration: 3000, position: "bottom-center" });
                    }
                });
        },
        confirmRemoveFromDepartment(groupKey) {
            const userIds = this.selectedUserIdsInGroup(groupKey);
            if (userIds.length === 0) return;
            const sub = this.getSubjectByKey(groupKey);
            if (!sub || !this.canUpdateUser) return;
            this.selectedUserIds = this.selectedUserIds.filter((id) => userIds.indexOf(id) === -1);
            this.$forceUpdate();
            const targetId = sub._id;
            const updateFns = userIds.map((uid) => () => {
                const user = this.usersList.find((u) => u._id === uid);
                if (!user) return Promise.resolve();
                const next = (user.subjects || []).filter((id) => id !== targetId);
                return this.updateUser({ user: Object.assign({}, user, { subjects: next }), siteId: this.site._id });
            });
            this.runBulkUpdates(updateFns, () => this.loadUsers());
        },
        getOtherSubjects(groupKey) {
            const sub = this.getSubjectByKey(groupKey);
            const currentId = sub ? sub._id : null;
            return this.undeleted.filter((s) => s._id !== currentId);
        },
        moveUsersDirectly(groupKey, targetSubjectId) {
            const sub = this.getSubjectByKey(groupKey);
            if (!sub || !targetSubjectId || !this.canUpdateUser) return;
            const userIds = this.selectedUserIdsInGroup(groupKey).slice();
            if (userIds.length === 0) return;
            this.selectedUserIds = this.selectedUserIds.filter((id) => userIds.indexOf(id) === -1);
            this.moveLoading = true;
            const sourceId = sub._id;
            var self = this;
            var updateFns = userIds.map(function (uid) {
                return function () {
                    var user = self.usersList.find(function (u) { return u._id === uid; });
                    if (!user) return Promise.resolve();
                    if (targetSubjectId === '__all__') {
                        return self.updateUser({ user: Object.assign({}, user, { subjects: [] }), siteId: self.site._id });
                    }
                    var current = user.subjects || [];
                    var next = current.filter(function (id) { return id !== sourceId; });
                    if (next.indexOf(targetSubjectId) === -1) next = next.concat([targetSubjectId]);
                    return self.updateUser({ user: Object.assign({}, user, { subjects: next }), siteId: self.site._id });
                };
            });
            this.runBulkUpdates(updateFns, function () {
                self.$toasted.show(self.$t("message.departmentUsersUpdated"), { duration: 3000, position: "bottom-center" });
                self.loadUsers();
                self.moveLoading = false;
            });
        },
        copyUsersToDepartment(groupKey, targetSubjectId) {
            var sub = this.getSubjectByKey(groupKey);
            if (!sub || !targetSubjectId || !this.canUpdateUser) return;
            var userIds = this.selectedUserIdsInGroup(groupKey).slice();
            if (userIds.length === 0) return;
            this.selectedUserIds = this.selectedUserIds.filter(function (id) { return userIds.indexOf(id) === -1; });
            var self = this;
            var updateFns = userIds.map(function (uid) {
                return function () {
                    var user = self.usersList.find(function (u) { return u._id === uid; });
                    if (!user) return Promise.resolve();
                    if (targetSubjectId === '__all__') {
                        return self.updateUser({ user: Object.assign({}, user, { subjects: [] }), siteId: self.site._id });
                    }
                    var current = user.subjects || [];
                    if (current.indexOf(targetSubjectId) !== -1) return Promise.resolve();
                    var next = current.concat([targetSubjectId]);
                    return self.updateUser({ user: Object.assign({}, user, { subjects: next }), siteId: self.site._id });
                };
            });
            this.runBulkUpdates(updateFns, function () {
                self.$toasted.show(self.$t("message.departmentUsersUpdated"), { duration: 3000, position: "bottom-center" });
                self.loadUsers();
            });
        },
        copySingleUserToDepartment(user, targetSubjectId) {
            if (!targetSubjectId || !this.canUpdateUser) return;
            var current = user.subjects || [];
            if (current.indexOf(targetSubjectId) !== -1) {
                this.$toasted.show(this.$t("message.userAlreadyInDepartment"), { duration: 3000, position: "bottom-center" });
                return;
            }
            var next = current.concat([targetSubjectId]);
            this.updateUser({ user: Object.assign({}, user, { subjects: next }), siteId: this.site._id })
                .then(() => {
                    this.$toasted.show(this.$t("message.departmentUsersUpdated"), { duration: 3000, position: "bottom-center" });
                    this.loadUsers();
                })
                .catch((err) => {
                    log.error(err);
                    this.$toasted.error(this.$t("message.error"), { duration: 3000, position: "bottom-center" });
                });
        },
        moveSingleUser(user, groupKey, targetSubjectId) {
            var sub = this.getSubjectByKey(groupKey);
            if (!sub || !targetSubjectId || !this.canUpdateUser) return;
            var newSubjects;
            if (targetSubjectId === '__all__') {
                newSubjects = [];
            } else {
                var sourceId = sub._id;
                var current = user.subjects || [];
                newSubjects = current.filter(function (id) { return id !== sourceId; });
                if (newSubjects.indexOf(targetSubjectId) === -1) newSubjects = newSubjects.concat([targetSubjectId]);
            }
            var self = this;
            this.updateUser({ user: Object.assign({}, user, { subjects: newSubjects }), siteId: this.site._id })
                .then(function () {
                    self.$toasted.show(self.$t("message.departmentUsersUpdated"), { duration: 3000, position: "bottom-center" });
                    self.loadUsers();
                })
                .catch(function (err) {
                    log.error(err);
                    self.$toasted.error(self.$t("message.error"), { duration: 3000, position: "bottom-center" });
                });
        },
        assignUsersToDepartment(groupKey, targetSubjectId) {
            if (!targetSubjectId || !this.canUpdateUser) return;
            var userIds = this.selectedUserIdsInGroup(groupKey).slice();
            if (userIds.length === 0) return;
            this.selectedUserIds = this.selectedUserIds.filter(function (id) { return userIds.indexOf(id) === -1; });
            this.moveLoading = true;
            var self = this;
            var updateFns = userIds.map(function (uid) {
                return function () {
                    var user = self.usersList.find(function (u) { return u._id === uid; });
                    if (!user) return Promise.resolve();
                    return self.updateUser({ user: Object.assign({}, user, { subjects: [targetSubjectId] }), siteId: self.site._id });
                };
            });
            this.runBulkUpdates(updateFns, function () {
                self.$toasted.show(self.$t("message.departmentUsersUpdated"), { duration: 3000, position: "bottom-center" });
                self.loadUsers();
                self.moveLoading = false;
            });
        },
        assignSingleUserToDepartment(user, targetSubjectId) {
            if (!targetSubjectId || !this.canUpdateUser) return;
            var self = this;
            this.updateUser({ user: Object.assign({}, user, { subjects: [targetSubjectId] }), siteId: this.site._id })
                .then(function () {
                    self.$toasted.show(self.$t("message.departmentUsersUpdated"), { duration: 3000, position: "bottom-center" });
                    self.loadUsers();
                })
                .catch(function (err) {
                    log.error(err);
                    self.$toasted.error(self.$t("message.error"), { duration: 3000, position: "bottom-center" });
                });
        },
        runBulkUpdates(updateFns, onDone) {
            const total = updateFns.length;
            this.bulkProgress = { visible: true, current: 0, total, loading: true };
            let current = 0;
            const run = (index) => {
                if (index >= updateFns.length) {
                    this.bulkProgress.loading = false;
                    if (onDone) onDone();
                    setTimeout(() => this.hideBulkProgress(), 500);
                    return Promise.resolve();
                }
                return updateFns[index]()
                    .then(() => {
                        current++;
                        this.bulkProgress.current = current;
                        this.$forceUpdate();
                        return run(index + 1);
                    })
                    .catch((err) => {
                        log.error(err);
                        return run(index + 1);
                    });
            };
            run(0);
        },
        hideBulkProgress() {
            this.bulkProgress = { visible: false, current: 0, total: 0, loading: false };
            this.$forceUpdate();
        },
        filterOption(input, option) {
            const text = option.componentOptions && option.componentOptions.children && option.componentOptions.children[0];
            const str = (text && text.text) ? text.text : "";
            return str.toLowerCase().indexOf((input || "").toLowerCase()) >= 0;
        },
        loadUsers() {
            return this.getUsers({
                perPage: 9999,
                page: 1,
                query: undefined,
                sortBy: "name",
                sortOrder: "ascend",
                updateStore: true,
            }).then((res) => {
                this.usersList = (res.data && res.data.data) ? res.data.data : [];
                this.usersLoaded = true;
            }).catch(() => {
                this.usersList = [];
            });
        },
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        showModalWithoutEdit() {
            this.editMode = false;
            this.showModal();
        },
        addNewSubjectTitle() {
            Object.keys(this.subjectTitle).forEach((key) => {
                this.subject.languageContent[key].name = this.subjectTitle[key];
            });
        },
        onSearch(value) {
            this.searchText = value;
        },
        get() {
            this.isBusy = true;
            this.getSubjects({ perPage: this.paginationCursor.perPage, page: this.paginationCursor.page }).then((response) => {
                this.paginationCursor.total = response.data.total;
                this.isBusy = false;
                const keys = Object.keys(this.departmentGroups);
                if (this.expandedGroups.size === 0 && keys.length) {
                    this.expandedGroups = new Set(keys);
                    this.$forceUpdate();
                }
                if (this.queryAction === "create") this.isModalShow = true;
            });
        },
        edit(id) {
            const found = this.undeleted.find((p) => p._id === id);
            if (found) {
                this.subject = _.cloneDeep(found);
                this.editMode = true;
                this.editID = id;
                this.showModal();
            }
        },
        publishNewSubjects() {
            sendSocketMessage("user publish-subjects", { siteId: this.site._id });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deleteDepartmentQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteSubject(id)
                        .then(() => {
                            this.$toasted.show(this.$t("message.departmentDeleted"), { duration: 3000, position: "bottom-center" });
                            const socketMessageId = this.isAdmin ? "admin subject-deleted" : "user subject-deleted";
                            sendSocketMessage(socketMessageId, { subjectId: id, siteId: this.site._id });
                            this.publishNewSubjects();
                            this.loadUsers();
                        })
                        .catch((err) => {
                            log.error(err.response, err.message);
                            const errMsg =
                                (err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : null) || err.message;
                            this.$toasted.error(errMsg, { duration: 3000, position: "bottom-center" });
                        });
                });
        },
        reset() {
            this.subject = _.cloneDeep(subjectInitialObject);
            this.submitClicked = false;
        },
        duplicateControl() {
            const isThereSameSubject = this.subjects.data.find(
                (x) =>
                    x.languageContent[this.$i18n.locale].name.toLowerCase() ===
                    this.subject.languageContent[this.$i18n.locale].name.toLowerCase()
            );
            const duplicateId = isThereSameSubject ? isThereSameSubject._id : null;
            const isThisSameSubject = duplicateId === this.editID;
            if (!isThisSameSubject && duplicateId !== null) {
                this.$toasted.error(this.$t("message.sameDepartment"), { duration: 3000, position: "bottom-center" });
                return;
            }
            this.handleSave();
        },
        handleSave() {
            this.submitClicked = true;
            this.$validator.validateAll().then((response) => {
                if (response) {
                    this.loading = true;
                    Object.keys(this.subject.languageContent).forEach((key) => {
                        if (!this.subject.languageContent[key].name) {
                            this.subject.languageContent[key].description =
                                this.subject.languageContent[this.defaultLang].description;
                            this.subject.languageContent[key].name = this.subject.languageContent[this.defaultLang].name;
                        }
                    });
                    const result = this.subject._id
                        ? this.updateSubject(this.subject)
                        : this.createSubject(this.subject);
                    result
                        .then(() => {
                            this.hideModal();
                            this.$toasted.show(this.$t("message.departmentSaved"), { duration: 3000, position: "bottom-center" });
                            this.get();
                            this.reset();
                            this.publishNewSubjects();
                            sendSocketMessage("team refresh-chats", { siteId: this.site._id });
                        })
                        .finally(() => {
                            this.loading = false;
                            this.submitClicked = false;
                        });
                } else {
                    this.loading = false;
                }
            });
        },
    },
    mounted() {
        this.get();
        this.loadUsers().then(() => {
            const keys = Object.keys(this.departmentGroups);
            if (keys.length && this.expandedGroups.size === 0) {
                this.expandedGroups = new Set(keys);
                this.$forceUpdate();
            }
        });
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
};
</script>

<style lang="scss">
.departments-page {
    padding: 0 16px;
    height: 100%;
}
.departments-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    .departments-search {
        max-width: 320px;
    }
    &__right {
        display: flex;
        align-items: center;
        gap: 8px;
    }
}
.add-users-modal-wrap {
    .ant-modal {
        top: 80px;
    }
}
.department-groups {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.department-card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    overflow: hidden;
    &--no-dept {
        .department-card__header {
            background: #fafafa;
            border-left: 4px solid #8c8c8c;
        }
    }
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        cursor: pointer;
        user-select: none;
        border-left: 4px solid transparent;
        background: #fff;
        &:hover {
            background: #fafafa;
        }
    }
    &__title-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    &__toggle {
        font-size: 24px;
        color: #666;
    }
    &__name {
        font-weight: 600;
        font-size: 15px;
        color: #333;
    }
    &__count {
        background: #e8e8e8;
        padding: 2px 10px;
        border-radius: 12px;
        font-size: 12px;
        color: #666;
    }
    &__actions {
        display: flex;
        gap: 4px;
        .action-btn {
            padding: 4px;
            &--danger {
                color: #ff4d4f;
            }
        }
    }
    &__body {
        padding: 12px 16px;
        border-top: 1px solid #e8e8e8;
        background: #fafafa;
    }
    &__user-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
    }
    &__user-list {
        max-height: 320px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    &__empty {
        color: #8c8c8c;
        font-size: 13px;
        padding: 16px;
        text-align: center;
    }
}
.department-user-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid transparent;
    &.selected {
        border-color: #1bc5bd;
        background: #e6fffb;
    }
    &__name {
        font-size: 13px;
        color: #333;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    &__actions {
        flex-shrink: 0;
    }
}
.add-users-modal,
.move-modal {
    .add-users-modal__footer,
    .move-modal__footer {
        margin-top: 16px;
        display: flex;
        justify-content: flex-end;
        gap: 8px;
    }
}
.progress-modal {
    &__text {
        margin-top: 12px;
        text-align: center;
        color: #666;
    }
}

.subject-form-modal #modal-inner {
    overflow-y: scroll;
    overflow-x: hidden;
    max-height: 70vh;
}
</style>
